import { PcbNodeTypes } from "@buildwithflux/models";
import { getAncestorUidByType } from "../../../../nodes";
import { getNodeAbsVertexPosition, TraceCorners } from "../../../../pcb";
import { createCircleOrOblongShape } from "../../../../shapes";
import { getModule, getRotatedPosition, toMm } from "./support/utils";
export const mapRouteSegment = (routeSegment, context, postprocess, pcbNodes) => {
    const { bakedRules } = routeSegment;
    if (!bakedRules) {
        return null;
    }
    const layer = context.getKiCadLayerByLayerIdentifier(routeSegment.uid, bakedRules.layer);
    if (!layer) {
        return null;
    }
    const netId = getAncestorUidByType(pcbNodes, routeSegment.uid, PcbNodeTypes.net);
    const netKiCad = netId && context.nodes.net?.[netId];
    if (netId && !netKiCad) {
        throw new Error(`Cannot find mapped net for segment ${routeSegment.uid} (net ID: ${netId})`);
    }
    const traceParentNodeId = routeSegment.parentUid;
    const traceParentNode = traceParentNodeId && pcbNodes[traceParentNodeId];
    if (!traceParentNode || traceParentNode.type === PcbNodeTypes.root) {
        throw new Error(`Trace ${routeSegment.uid} has no parent`);
    }
    const start = getRotatedPosition(bakedRules.startPosition, bakedRules.rotationRelativeToDocument);
    const end = getRotatedPosition(bakedRules.endPosition, bakedRules.rotationRelativeToDocument);
    const net = netKiCad ? { net_number: netKiCad.net_number } : undefined;
    const segments = [];
    const width = toMm(bakedRules.size.x);
    const hasSolderMaskOpening = (bakedRules.layer === "Top" || bakedRules.layer === "Bottom") && bakedRules.solderMaskExpansion !== undefined;
    if (hasSolderMaskOpening) {
        postprocess(() => {
            const module = getModule(bakedRules.layer, context);
            const length = start.distanceTo(end) + bakedRules.size.x * 2;
            const angle = -Math.atan2(end.y - start.y, end.x - start.x);
            const at = {
                x: toMm(bakedRules.positionRelativeToDocument.x + (start.x + end.x) / 2),
                y: -toMm(bakedRules.positionRelativeToDocument.y + (start.y + end.y) / 2),
            };
            const size = {
                x: toMm(length) - toMm(bakedRules.size.x) + toMm(bakedRules.solderMaskExpansion) * 2,
                y: toMm(bakedRules.size.x) + toMm(bakedRules.solderMaskExpansion) * 2,
            };
            const shape = createCircleOrOblongShape(size, at, angle, 128, at);
            module.fp_poly = module.fp_poly ?? [];
            module.fp_poly.push({
                pts: shape.getPoints(128),
                layer: bakedRules.layer === "Top" ? "F.Mask" : "B.Mask",
                width: 0,
            });
        });
    }
    const traceNodeStart = TraceCorners.computeForVertex("start", routeSegment, pcbNodes, context.tracingGraph.getGraph());
    const traceNodeEnd = TraceCorners.computeForVertex("end", routeSegment, pcbNodes, context.tracingGraph.getGraph());
    // Insert curve for segment start if present
    for (const segment of TraceCorners.segmentGenerator(traceNodeStart?.arc)) {
        const traceVectorStart = getNodeAbsVertexPosition({ ...segment.start, z: 0 }, routeSegment, traceParentNode);
        const traceVectorEnd = getNodeAbsVertexPosition({ ...segment.end, z: 0 }, routeSegment, traceParentNode);
        segments.push({
            start: { x: toMm(traceVectorStart.x), y: -toMm(traceVectorStart.y) },
            end: { x: toMm(traceVectorEnd.x), y: -toMm(traceVectorEnd.y) },
            layer,
            width,
            net,
        });
    }
    // Insert curve for segment end if present
    for (const segment of TraceCorners.segmentGenerator(traceNodeEnd?.arc)) {
        const traceVectorStart = getNodeAbsVertexPosition({ ...segment.start, z: 0 }, routeSegment, traceParentNode);
        const traceVectorEnd = getNodeAbsVertexPosition({ ...segment.end, z: 0 }, routeSegment, traceParentNode);
        segments.push({
            start: { x: toMm(traceVectorStart.x), y: -toMm(traceVectorStart.y) },
            end: { x: toMm(traceVectorEnd.x), y: -toMm(traceVectorEnd.y) },
            layer,
            width,
            net,
        });
    }
    const traceStart = traceNodeStart?.vertex ?? routeSegment.bakedRules.startPosition;
    const traceEnd = traceNodeEnd?.vertex ?? routeSegment.bakedRules.endPosition;
    const traceVectorStart = getNodeAbsVertexPosition(traceStart, routeSegment, traceParentNode);
    const traceVectorEnd = getNodeAbsVertexPosition(traceEnd, routeSegment, traceParentNode);
    segments.push({
        start: { x: +toMm(traceVectorStart.x), y: -toMm(traceVectorStart.y) },
        end: { x: +toMm(traceVectorEnd.x), y: -toMm(traceVectorEnd.y) },
        layer,
        width,
        net,
    });
    return segments;
};
//# sourceMappingURL=mapRouteSegment.js.map